/**
 * PATH: packages/bills/src/reclassify.ts
 * FILE_ID: BILLS-PKG-RECLASSIFY-001
 * PURPOSE: Single source of truth for the "this isn't a bill" reclassify menu.
 *
 * WHY: The Reclassify dropdown lives in BOTH BillDataPanel (right
 *      rail) and BillsListPanel (right-click context menu). Without
 *      a shared constant the two menus drift — backend rules engine
 *      (organisation/services/classification_service/config.py)
 *      defines the canonical category set; this file mirrors only
 *      the user-pickable subset that's meaningful when leaving the
 *      Bills queue.
 *
 * WHEN TO ADD AN OPTION: only categories the user actually wants to
 *      see during AP triage. Example: a tax invoice we issued is
 *      `invoice` (AR); a marketing blast is `spam`; a remittance
 *      advice is `payment`. We deliberately omit niche categories
 *      like `safety` or `timesheet` — those rarely sit in the AP
 *      queue and would just clutter the menu.
 *
 * BACKEND CONTRACT: each `value` MUST exist in
 *      organisation.services.classification_service.config.EMAIL_CATEGORIES
 *      (otherwise the locked override row stays orphaned and the AI
 *      classifier falls through to a different category on next pass).
 */

export interface ReclassifyOption {
  value: string;
  label: string;
  /** One-line description of when to pick this. */
  hint: string;
}

/**
 * The "Not a bill, it's actually..." menu items, ordered by how often
 * users hit them in our triage logs (Outgoing invoice → Spam →
 * Payment advice → General).
 */
export const RECLASSIFY_OPTIONS: ReadonlyArray<ReclassifyOption> = [
  {
    value: 'invoice',
    label: 'Our outgoing invoice',
    hint: 'A tax invoice we sent to a client (accounts receivable, not AP).',
  },
  {
    value: 'payment',
    label: 'Payment advice / remittance',
    hint: 'A "we paid" or "we received payment" notification, not a fresh bill.',
  },
  {
    value: 'quote_submission',
    label: 'Outgoing quote',
    hint: 'A quote / pricing email we sent — belongs in the Quotes pipeline.',
  },
  {
    value: 'supplier',
    label: 'Supplier note (not a bill)',
    hint: 'Account status, statement-only, dunning chase, price-list update.',
  },
  {
    value: 'general',
    label: 'General correspondence',
    hint: 'Generic business email — no AP action required.',
  },
  {
    value: 'spam',
    label: 'Spam / marketing',
    hint: 'Promotional or unsolicited mass email.',
  },
];
